import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { queryEvents } from '../schema';
import { useState, useMemo } from 'react';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { computeItemsToCopy } from '../utils/copyPackingList';

interface Props {
    open: boolean;
    onClose: () => void;
    eventId: string;
    onCopied: () => void;
}

export default function CopyPackingListDialog({ open, onClose, eventId, onCopied }: Props) {
    const { data: eventsData, isLoading: eventsLoading } = queryEvents();
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [includeWishList, setIncludeWishList] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    // Get all events with packing lists
    const eventsWithPackingLists = useMemo(() => {
        if (!eventsData?.events) return [];
        return eventsData.events
            .filter(event => {
                // Filter out current event
                if (event.id === eventId) return false;
                // We'll need to check if they have packing lists
                // For now, return all events (we'll filter by actual packing list data)
                return true;
            })
            .sort((a, b) => {
                // Sort by start date DESC (most recent first)
                const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
                const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
                return dateB - dateA;
            });
    }, [eventsData, eventId]);

    // Query packing lists for events (we'll need to query each one)
    // For efficiency, we'll query all packing list items and group by event
    const { data: allPackingListsData } = db.useQuery({
        events: {
            packingListItems: {
                itemType: {
                    category: {},
                },
            },
        },
    });

    // Get events with their packing list counts
    const eventsWithCounts = useMemo(() => {
        if (!allPackingListsData?.events) return [];

        return eventsWithPackingLists
            .map(event => {
                const eventData = allPackingListsData.events.find(e => e.id === event.id);
                const packingListItems = (eventData?.packingListItems || []) as any[];
                const trailers = packingListItems.filter(item => item.itemType?.category?.name === 'Trailers');
                const otherItems = packingListItems.filter(item => item.itemType?.category?.name !== 'Trailers');

                return {
                    event,
                    itemCount: otherItems.length,
                    trailerCount: trailers.reduce((sum, item) => sum + (item.quantity || 0), 0),
                    packingListItems,
                };
            })
            .filter(item => item.itemCount > 0 || item.trailerCount > 0); // Only show events with packing lists
    }, [eventsWithPackingLists, allPackingListsData]);

    const selectedEvent = eventsWithCounts.find(e => e.event.id === selectedEventId);

    const handleCopy = async () => {
        if (!selectedEvent) return;

        setIsCopying(true);

        try {
            // Build packing list quantities per item type
            const packByItemType = new Map<string, number>();
            for (const item of selectedEvent.packingListItems) {
                const itemTypeId = item.itemType?.id;
                if (!itemTypeId) continue;
                const qty = item.quantity || 0;
                packByItemType.set(itemTypeId, (packByItemType.get(itemTypeId) ?? 0) + qty);
            }

            // If including wish list: copy quantity = pack - wish; only add if > 0
            let wishByItemType = new Map<string, number>();
            if (includeWishList && selectedEventId) {
                const { data: wishListData } = await db.queryOnce({
                    events: {
                        $: {
                            where: {
                                id: selectedEventId,
                            },
                        },
                        wishListItems: {
                            itemType: {},
                        },
                    },
                });
                const wishListItems = wishListData?.events?.[0]?.wishListItems || [];
                for (const wishItem of wishListItems) {
                    const itemTypeId = wishItem.itemType?.id;
                    if (!itemTypeId) continue;
                    const qty = wishItem.quantity || 0;
                    wishByItemType.set(itemTypeId, (wishByItemType.get(itemTypeId) ?? 0) + qty);
                }
            }
            const itemsArray = computeItemsToCopy(packByItemType, wishByItemType, includeWishList && !!selectedEventId);

            // Create transactions for all items
            const txs: any[] = [];
            for (const { itemTypeId, quantity } of itemsArray) {
                const newItemId = id();
                txs.push(
                    db.tx.packingListItems[newItemId].update({
                        quantity,
                        confirmed: false,
                    }),
                    db.tx.packingListItems[newItemId].link({ itemType: itemTypeId }),
                    db.tx.packingListItems[newItemId].link({ event: eventId })
                );
            }

            await db.transact(txs);
            onCopied();
            handleClose();
        } catch (error) {
            console.error('Error copying packing list:', error);
            // TODO: Show error toast
        } finally {
            setIsCopying(false);
        }
    };

    const handleClose = () => {
        setSelectedEventId(null);
        setIncludeWishList(false);
        onClose();
    };

    const formatDateRange = (event: any) => {
        if (!event.startDate && !event.endDate) return 'No dates';
        const start = event.startDate ? new Date(event.startDate).toLocaleDateString() : '';
        const end = event.endDate ? new Date(event.endDate).toLocaleDateString() : '';
        if (start && end) return `${start} - ${end}`;
        return start || end;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Copy Packing List from Previous Event</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {eventsLoading ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                ) : eventsWithCounts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            No events with packing lists
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <List>
                                {eventsWithCounts.map(({ event, itemCount, trailerCount }) => (
                                    <ListItemButton
                                        key={event.id}
                                        selected={selectedEventId === event.id}
                                        onClick={() => setSelectedEventId(event.id)}
                                    >
                                        <ListItemText
                                            primary={event.name}
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDateRange(event)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {itemCount} items, {trailerCount} {trailerCount === 1 ? 'trailer' : 'trailers'}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>

                        {selectedEvent && (
                            <FormControlLabel
                                control={<Checkbox checked={includeWishList} onChange={e => setIncludeWishList(e.target.checked)} />}
                                label="Include wish list items from selected event"
                            />
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCopy} disabled={!selectedEventId || isCopying}>
                    {isCopying ? 'Copying...' : 'Copy Packing List'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
